import {
  getSimilarListings,
  postCannedTemplate,
  postSimilarListings,
  postSimilarListingsPublic,
  putCannedTemplate,
  tenantStartChat
} from '@/ports'
import { FetchResponse } from '@/ports/api'
import type { InquiryMessage } from '@/features/Listings/contact-form/useInquiryMessage'
import { queryClient } from '@/components/query-client'

type GetSimilarListingsResponse = {
  message: string
  result_count: number
  search_tracking_id: string
  similar_listings: { listing_id: string }[]
}

export type ContactSimilarListingsResponse = {
  success_count: number
  failed_count: number
  successful_listings: string[]
  failed_listings: any[] // don't really care
}

export type GeneralError = {
  message_key?: string
  message: string
}

export const contactSimilarListingFunc = async (params: {
  isLoggedIn: boolean
  listingId: string
  formValues: any
}): Promise<{
  ok: boolean
  message?: string
  result_count?: number
  success_count?: number
  failed_count?: number
  successful_listings?: string[]
  failed_listings?: any[]
}> => {
  const { isLoggedIn, listingId, formValues } = params
  const getResult = (await getSimilarListings(
    { listingId },
    { limit: 9 }
  )) as FetchResponse<GetSimilarListingsResponse | GeneralError>

  if (!getResult.response.ok) {
    return { ok: false, message: (getResult.body as GeneralError)?.message }
  }

  const getSimilarListingsResponse =
    getResult.body as GetSimilarListingsResponse

  if (getSimilarListingsResponse?.result_count <= 0) {
    return { ok: true, result_count: 0 }
  }

  let contactResult

  if (isLoggedIn) {
    contactResult = (await postSimilarListings({
      message: formValues.message,
      move_in_date: formValues.move_in_date,
      search_tracking_id: getSimilarListingsResponse?.search_tracking_id,
      listings: getSimilarListingsResponse?.similar_listings
        .slice(0, 3)
        .map(({ listing_id }) => listing_id)
        .join(',')
    })) as FetchResponse<ContactSimilarListingsResponse | GeneralError>
  } else {
    contactResult = (await postSimilarListingsPublic(
      {
        body: {
          listings: getSimilarListingsResponse?.similar_listings
            .slice(0, 3)
            .map(({ listing_id }) => listing_id)
            .join(','),
          message: formValues.message,
          move_in_date: formValues.move_in_date,
          first_name: formValues.first_name,
          last_name: formValues.last_name,
          email: formValues.email,
          search_tracking_id: getSimilarListingsResponse?.search_tracking_id
        }
      },
      undefined,
      undefined,
      {
        headers: {
          'X-Captcha-Token': formValues.captcha ?? ''
        }
      }
    )) as FetchResponse<ContactSimilarListingsResponse | GeneralError>
  }

  if (!contactResult.response.ok) {
    return {
      ok: false,
      message: (contactResult.body as GeneralError)?.message
    }
  }

  const { failed_count, success_count } =
    contactResult.body as ContactSimilarListingsResponse

  if (failed_count > 0) {
    return {
      ok: false,
      message: 'Unable to contact some listings'
    }
  }

  return { ok: true, ...contactResult.body, result_count: success_count }
}

export const sendInquiryMessage = async (data: {
  isLoggedIn: boolean
  listingId: string
  formValues: any
}): Promise<{ ok: boolean; message?: string }> => {
  const { response, body }: any = await tenantStartChat(data.formValues, {
    listingId: data.listingId
  })

  if (!response.ok) {
    return { ok: false, message: body?.message }
  }

  return { ok: true }
}

export const saveCustomMessage = async (params: {
  isLoggedIn: boolean
  inquiryMessage: InquiryMessage
  formValues: any
}) => {
  const { isLoggedIn, inquiryMessage, formValues } = params
  if (isLoggedIn) {
    if (!inquiryMessage?.id) {
      // create new inquiry message
      postCannedTemplate({
        body: { content: formValues.message, template_type: 'inquiries' }
      })
    } else {
      if (formValues.message != inquiryMessage.content) {
        putCannedTemplate(
          {
            body: { content: formValues.message }
          },
          { templateId: inquiryMessage.id }
        )
      }
    }
  } else {
    localStorage.setItem('inquiry-message', formValues.message)
  }

  queryClient.removeQueries(['useInquiryMessage'])
}
